import type { Dictionary } from './vi';

/** English — the secondary locale. Structurally identical to `vi` (enforced by the type). */
export const en: Dictionary = {
  common: {
    appName: 'VideoGrab Pro',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    delete: 'Delete',
    remove: 'Remove',
    edit: 'Edit',
    rename: 'Rename',
    duplicate: 'Duplicate',
    open: 'Open',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    retry: 'Try again',
    refresh: 'Refresh',
    loading: 'Loading…',
    download: 'Download',
    upload: 'Upload',
    copy: 'Copy',
    copied: 'Copied',
    select: 'Select',
    selectAll: 'Select all',
    deselectAll: 'Clear selection',
    selected_one: '{{count}} selected',
    selected_other: '{{count}} selected',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    more: 'More',
    less: 'Less',
    viewAll: 'View all',
    loadMore: 'Load more',
    actions: 'Actions',
    status: 'Status',
    date: 'Date',
    name: 'Name',
    type: 'Type',
    size: 'Size',
    duration: 'Duration',
    platform: 'Platform',
    author: 'Author',
    credits: 'Credits',
    creditsCost: '− {{count}} credits',
    free: 'Free',
    unlimited: 'Unlimited',
    optional: 'optional',
    required: 'required',
    reportIssue: 'Report a problem',
    errorCode: 'Error code: {{code}}',
    justNow: 'just now',
    minutesAgo: '{{count}}m ago',
    hoursAgo: '{{count}}h ago',
    daysAgo: '{{count}}d ago',
    unknown: 'Unknown',
  },

  nav: {
    groupWork: 'Work',
    groupResults: 'Results',
    groupAdmin: 'Admin',
    overview: 'Overview',
    download: 'Download',
    batch: 'Batch',
    explorer: 'Explorer',
    projects: 'Projects',
    library: 'Library',
    libraryDownloads: 'Downloads',
    libraryExports: 'Exports',
    activity: 'Activity',
    workspaces: 'Workspaces',
    adminUsers: 'Users',
    adminIntegrations: 'Integrations',
    adminFeedback: 'Feedback',
    account: 'Account',
    support: 'Support',
    locked: 'Not enabled',
    collapse: 'Collapse sidebar',
    expand: 'Expand sidebar',
    skipToContent: 'Skip to main content',
  },

  workspaces: {
    title: 'Content workspaces',
    subtitle: 'Separate VideoGrab into independent areas and connect each one to the right SocialForge workspace.',
    current: 'Current workspace',
    create: 'Create workspace',
    createTitle: 'Create a workspace',
    createHint: 'Projects, downloads, batches, and libraries are isolated in this workspace.',
    description: 'Description',
    areas: 'Content areas',
    areasHint: 'Switching workspace changes the full working context.',
    isolationHint: 'All production data on the current screen belongs to this workspace.',
    noDescription: 'No description yet.',
    default: 'Default',
    projectCount: '{{count}} projects',
    downloadCount: '{{count}} downloads',
    batchCount: '{{count}} batches',
    socialforgeTitle: 'SocialForge connection',
    socialforgeHint: 'Each workspace uses its own producer key. The key is encrypted and never returned to the browser.',
    connected: 'Connected',
    notConnected: 'Not connected',
    baseUrl: 'Internal SocialForge URL',
    publicUrl: 'Browser-facing URL',
    apiKey: 'Producer API key',
    apiKeyHint: 'Create a producer key with the Publish permission set in SocialForge.',
    apiKeyReplaceHint: 'Enter the producer key again to change configuration. The saved key is never filled back here.',
    connect: 'Connect',
    replaceConnection: 'Update connection',
    check: 'Check connection',
    disconnect: 'Disconnect',
    connectionSaved: 'SocialForge connection saved and verified.',
    connectionOk: 'Connection healthy · {{count}} channels available.',
    disconnected: 'SocialForge disconnected.',
    switchTo: 'Switch workspace',
    manage: 'Manage workspaces',
    disconnectTitle: 'Disconnect SocialForge?',
    disconnectBody:
      'The “{{workspace}}” workspace will not be able to hand content to SocialForge until you enter a producer key again. Posts already handed over are not affected.',
  },

  socialforge: {
    send: 'Send to SocialForge',
    title: 'Content handoff',
    subtitle: 'Send this video to channels in the {{workspace}} workspace.',
    notConnected: 'This workspace is not connected to SocialForge.',
    notConnectedHint: 'Open workspace settings and enter the producer key dedicated to this content area.',
    openSettings: 'Open workspace settings',
    channel: 'Target channel',
    noChannels: 'No active SocialForge-owned channel is available',
    videoSource: 'Video to hand off',
    sourceProject: 'Project source video',
    sourceExport: 'Export · {{date}}',
    caption: 'Post caption',
    confirmSend: 'Upload and create publication',
    sent: 'Content handed off to SocialForge.',
    history: 'Handoff history',
    openPublication: 'Open publication',
    reconcile: 'Sync',
    supplyShort: '{{pending}} waiting · floor {{floor}}',
    supplyOk: 'The channel has {{pending}} posts waiting, at its floor.',
    supplyLow: 'The channel has only {{pending}} posts waiting, {{short}} short of its floor.',
    supplyRule: 'SocialForge posts {{perDay}} a day and needs at least {{floor}} waiting at all times.',
    warmingUp: 'A new Page, posting less while it warms up.',
    timingNote: 'No time to pick: SocialForge plans each post by its workspace schedule.',
  },

  datePicker: {
    anyTime: 'Any time',
    today: 'Today',
    last7: 'Last 7 days',
    last30: 'Last 30 days',
    thisMonth: 'This month',
    clear: 'Clear dates',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    pickStart: 'Pick a start date',
    pickEnd: 'From {{date}} — pick an end date',
    weekdays: 'Mo,Tu,We,Th,Fr,Sa,Su',
  },

  player: {
    label: 'Video player',
    play: 'Play',
    pause: 'Pause',
    mute: 'Mute',
    unmute: 'Unmute',
    volume: 'Volume',
    seek: 'Seek',
    speed: 'Playback speed',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    error: 'This video could not be played.',
  },

  colorPicker: {
    open: 'Open colour picker',
    saturationBrightness: 'Saturation and brightness',
    hue: 'Hue',
    eyedropper: 'Pick a colour from the screen',
    presets: 'Suggested colours',
  },

  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    email: 'Email',
    password: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    signingIn: 'Signing in…',
    welcomeBack: 'Sign in to continue',
    subtitle: 'Download, caption and publish video — in one place.',
    forcedTitle: 'Change your password to continue',
    forcedDescription:
      'This account still uses the default password. Set your own before continuing.',
    changePassword: 'Change password',
    passwordUpdated: 'Password changed. All other sessions have been signed out.',
    sessionExpiredTitle: 'Your session expired',
    sessionExpiredBody: 'Sign in again to continue. Your work is kept as it was.',
    reauthenticate: 'Sign in again',
    lockedUntil: 'Account temporarily locked. Try again at {{time}}.',
    rules: {
      title: 'Password must have:',
      length: 'at least 8 characters',
      upper: 'an uppercase letter',
      lower: 'a lowercase letter',
      digit: 'a digit',
      notDefault: 'something other than the default',
      match: 'both fields matching',
    },
  },

  errors: {
    INVALID_CREDENTIALS: 'Email or password is incorrect.',
    TOKEN_EXPIRED: 'Your session has expired.',
    NOT_AUTHENTICATED: 'You need to sign in.',
    ACCOUNT_TEMPORARILY_LOCKED: 'Too many failed attempts. Locked for 15 minutes.',
    ACCOUNT_DISABLED: 'This account is disabled. Contact your administrator.',
    FORCE_CHANGE_PWD: 'You must change your password before continuing.',
    FORBIDDEN: 'You do not have permission to do this.',
    ADMIN_REQUIRED: 'Administrators only.',
    API_KEY_SCOPE_DENIED: 'API keys cannot be used for this endpoint.',
    FEATURE_DISABLED: 'This feature is not enabled for your account.',
    FEATURE_LIMIT_REACHED: 'Daily limit for this feature reached.',
    CREDIT_LIMIT_REACHED: 'Not enough credits for this action.',
    NOT_FOUND: 'Not found.',
    EXPIRED: 'This item has expired.',
    CONFLICT: 'This changed somewhere else.',
    VALIDATION_ERROR: 'Invalid input.',
    PAYLOAD_TOO_LARGE: 'File is too large.',
    UNSUPPORTED_MEDIA: 'Unsupported file format.',
    UPSTREAM_ERROR: 'An external service failed. Try again shortly.',
    NOT_CONFIGURED: 'This feature is not configured on the server.',
    INTERNAL_ERROR: 'Something went wrong. Please try again.',
    NETWORK_ERROR: 'Lost connection to the server.',
    TIMEOUT: 'The request timed out.',
    genericTitle: 'Something went wrong',
    notFoundTitle: 'Page not found',
    notFoundBody: 'This address does not exist or has moved.',
    forbiddenTitle: 'Not allowed',
    forbiddenBody: 'Your account cannot view this page.',
    serverTitle: 'System error',
    serverBody: 'We have logged this. Try reloading the page.',
    goHome: 'Back to Overview',
  },

  states: {
    emptyTitle: 'Nothing here yet',
    noMatchTitle: 'No matching results',
    noMatchBody: 'Try another keyword or a wider date range.',
    clearFilters: 'Clear filters',
    errorRetry: 'Try again',
    offline: 'You are offline',
    offlineBody: 'Check your connection and try again. Unsaved changes are kept.',
  },

  feature: {
    lockedTitle: '{{name}} is not enabled',
    lockedBody:
      'This feature is not part of your account’s permissions. Send a request and an administrator can enable it.',
    requestAccess: 'Request access',
    requestPrefill: 'Please enable the "{{name}}" feature for {{email}}.',
  },

  credits: {
    title: 'Credits',
    balance: 'Balance',
    remaining: '{{remaining}}/{{limit}}',
    usedToday: 'Used today',
    resetsAt: 'Resets at {{time}}',
    resetsDaily: 'Resets daily at 00:00 Vietnam time',
    dailyLimit: 'Daily limit',
    priceList: 'Price list',
    ledger: 'Transactions',
    usage14d: '14-day usage',
    noTransactions: 'No transactions yet',
    refund: 'Refund',
    insufficientTitle: 'Not enough credits',
    insufficientBody:
      'This costs {{cost}} credits but you have {{remaining}} left. Credits reset at {{time}}.',
    confirmTitle: 'Confirm credit usage',
    confirmBody: 'Action: {{action}}',
    confirmCost: 'Cost: {{cost}} credits',
    confirmBalance: 'Balance: {{before}} → {{after}}',
    confirmProceed: 'Continue',
    viewLedger: 'View transactions',
    usageTotal: 'Total: {{count}} credits',
    usageEmpty: 'No credits used in the last 14 days',
    rateGroup: {
      download: 'Downloads & explorer',
      edit: 'Editing',
    },
    rates: {
      download_douyin: 'Douyin video',
      download_tiktok: 'TikTok video',
      download_bilibili: 'Bilibili video',
      download_image: 'Image post',
      download_default: 'Other platform',
      profile_explorer: 'Profile explorer (per 20 videos)',
      edit_auto_caption: 'Auto captions',
      edit_ocr_detect: 'On-screen text (OCR)',
      edit_asr_transcribe: 'Speech-to-text',
      edit_translate: 'Caption translation',
      edit_tts: 'AI voice-over',
      edit_shorts_analyze: 'Clip suggestions',
      edit_export: 'Video export',
    },
  },

  jobs: {
    title: 'Jobs',
    tray: 'Job tray',
    running: 'Running',
    recent: 'Recent',
    none: 'Nothing is running',
    status: {
      pending: 'Queued',
      running: 'Processing',
      completed: 'Done',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    type: {
      download: 'Download',
      auto_caption: 'Auto-caption',
      ocr_detect: 'Detect on-screen text',
      ocr_batch: 'Detect on-screen text',
      asr_transcribe: 'Transcribe speech',
      translate: 'Translate captions',
      tts_batch: 'Generate voice-over',
      shorts_analyze: 'Suggest clips',
      export: 'Export video',
    },
    canLeave: 'You can leave this page — the job keeps running.',
    mustStay: 'Keep this page open until it finishes.',
    resultIn: 'The result will appear in {{place}}.',
    refunded: '{{count}} credits refunded',
    viewResult: 'View result',
    openProject: 'Open project',
    onYourPc: 'On your computer',
    completedToast: 'Done: {{title}}',
    failedToast: 'Failed: {{title}}',
  },

  overview: {
    title: 'Overview',
    greeting: 'Hello {{name}}',
    creditCard: 'Credits today',
    continueWorking: 'Continue working',
    continueEmpty: 'No projects open yet',
    runningJobs: 'Running',
    recentResults: 'Recent results',
    recentResultsEmpty: 'No exports yet',
    quickStart: 'Quick start',
    quickPaste: 'Paste a link to download',
    quickUpload: 'Upload a video to edit',
    quickExplore: 'Explore a profile',
    quickBatch: 'Batch processing',
    expiringTitle: 'Expiring soon',
    expiringBody_one: '{{count}} item expires within 48 hours.',
    expiringBody_other: '{{count}} items expire within 48 hours.',
    newUserTitle: 'Get started with VideoGrab Pro',
    newUserBody: 'Three steps to your first finished video.',
    step1: 'Paste a video link, or upload a video',
    step2: 'Create a project and edit the captions',
    step3: 'Export and download the result',
  },

  download: {
    title: 'Download',
    subtitle: 'Paste up to {{max}} links. Up to {{maxBatch}} items per download.',
    inputLabel: 'Video links',
    inputPlaceholder: 'Paste Douyin, TikTok or Bilibili links — one per line',
    analyze: 'Analyze',
    analyzing: 'Analyzing…',
    clear: 'Clear all',
    resultsTitle: 'Results',
    alreadyDownloaded: 'Downloaded',
    keepWatermark: 'Keep watermark',
    watermarkNote:
      'An already-downloaded video is reused, even if the watermark option differs from last time.',
    downloadSelected: 'Download {{count}}',
    editSelected: 'Edit {{count}}',
    maxSelection: 'You can download at most {{max}} items at a time.',
    syncWarning: 'Downloads run inline — keep this page open until they finish.',
    summary: 'Downloaded {{ok}}/{{total}}',
    summaryFailed: '{{count}} failed',
    summarySkipped: '{{count}} skipped — not enough credits',
    imagePost: 'Photo post',
    cannotEditImage: 'Image posts cannot be edited',
    editSkippedImages_one: 'Skipped {{count}} image post — only videos can be edited',
    editSkippedImages_other: 'Skipped {{count}} image posts — only videos can be edited',
    editImportFailed: 'This video could not be brought into the editor',
    editImportPartial_one: '{{count}} video could not be imported',
    editImportPartial_other: '{{count}} videos could not be imported',
    parseFailed: 'Could not read this link',
    emptyTitle: 'No links yet',
    emptyBody: 'Paste links above and press Analyze.',
    preview: 'Preview',
    historyTitle: 'Link history',
    historyHint: 'Every link you have analyzed or downloaded, newest first.',
    historySearch: 'Search by link, title or author',
    historyEmpty: 'No links in your history yet.',
    historyNoMatch: 'No links match that search.',
    historyUse: 'Use again',
    historyUsed: 'Added the link to the paste box',
    historyOpen: 'Open the original link',
    historyCopyHint: 'Click to copy the link',
    linkCopied: 'Link copied',
    linkCopyFailed: 'Could not copy the link',
    historyStatus: {
      parsed: 'Analyzed',
      completed: 'Downloaded',
      failed: 'Failed',
    },
  },

  projects: {
    title: 'Projects',
    subtitle: 'Where you edit your videos.',
    create: 'New project',
    createTitle: 'Create a project',
    fromDownload: 'From a download',
    fromDownloadHint: 'Pick a video from your library',
    fromUpload: 'Upload from this device',
    fromUploadHint: 'MP4, MOV, M4V, WEBM, MKV — up to {{max}}MB',
    fromUrl: 'From a link',
    fromUrlHint: 'Download it and open the editor straight away',
    dropzone: 'Drop a video here, or click to choose',
    uploading: 'Uploading… {{percent}}%',
    openEditor: 'Open editor',
    emptyTitle: 'No projects yet',
    emptyBody: 'Create one from a downloaded video, from your device, or from a link.',
    searchPlaceholder: 'Search project names',
    sortActivity: 'Recently active',
    sortCreated: 'Newest',
    sortName: 'By name',
    deleteTitle: 'Delete this project?',
    deleteBody:
      '"{{name}}", its video file and its exports will be deleted. Job history and credits are kept.',
    renameTitle: 'Rename project',
    duplicated: 'Duplicate created',
    extended: 'Project renewed',
    extend: 'Renew',
    previewUnavailable: 'This video cannot be previewed',
    detailSource: 'Source',
    detailFiles: 'Project files',
    detailExports: 'Exports',
    detailJobs: 'Job history',
    noExports: 'Not exported yet',
    noAssets: 'No music or voice-over yet',
    noJobs: 'No jobs yet',
    expiredTitle: 'This project has expired',
    expiredBody: 'Its files were cleaned up after a period of inactivity.',
    recreate: 'Recreate from source',
    sourceUpload: 'Upload',
    sourceDownload: 'From a download',
    sourceLink: 'From a link',
  },

  editor: {
    backToProject: 'Back to project',
    untitled: 'Untitled project',
    unsaved: 'Unsaved',
    savingNow: 'Saving…',
    savedAt: 'Saved {{time}}',
    saveFailed: 'Not saved — retrying in {{seconds}}s',
    saveRetry: 'Save now',
    undo: 'Undo',
    redo: 'Redo',
    menu: 'More',
    viewSrt: 'View captions (.srt)',
    downloadSrt: 'Download .srt',
    importSrt: 'Import .srt',
    shortcuts: 'Keyboard shortcuts',
    export: 'Export',
    conflictTitle: 'This project changed elsewhere',
    conflictBody:
      'Another tab or device saved this project. Reload to get the latest, or overwrite with what you have.',
    conflictReload: 'Reload',
    conflictOverwrite: 'Overwrite',
    leaveWarning: 'You have unsaved changes. Leaving now will lose them.',
    tabs: {
      captions: 'Captions',
      voice: 'Voice-over',
      music: 'Music',
      cut: 'Cuts',
      export: 'Export',
    },
    timeline: {
      captions: 'Captions',
      voice: 'Voice',
      music: 'Music',
      cuts: 'Cuts',
      ruler: 'Timeline position',
      snap: 'Snap to edges',
      hint: 'Ctrl+wheel to zoom · Shift+wheel to scroll',
      resize: 'Drag to resize the timeline',
      showCaptions: 'Show captions on the preview',
      hideCaptions: 'Hide captions on the preview',
      menu: {
        title: 'Caption actions',
        edit: 'Edit text',
        duplicate: 'Duplicate',
        duplicateMoved: 'Every row is taken at this time, so the copy went to the nearest free spot ({{time}})',
        split: 'Split at playhead',
        mergeNext: 'Merge with next line',
        moveUp: 'Move to the row above',
        moveDown: 'Move to the row below',
        autoLane: 'Choose the row automatically',
        startAtPlayhead: 'Set start to playhead',
        endAtPlayhead: 'Set end to playhead',
        copyText: 'Copy text',
        copied: 'Text copied',
        copyFailed: 'Could not copy the text',
      },
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fit: 'Fit',
    },
    canvas: {
      play: 'Play',
      pause: 'Pause',
      mute: 'Mute',
      unmute: 'Unmute',
      fullscreen: 'Fullscreen',
      exitFullscreen: 'Exit fullscreen',
      speed: 'Speed',
      noVideo: 'Could not load this project’s video',
    },
    captions: {
      autoTitle: 'Auto-caption',
      autoHint: 'Read the text on screen (or the speech), translate it, and put it straight into the video — one step.',
      autoTranslateToggle: 'Translate to Vietnamese',
      autoTranslateHint: 'Turn off to keep the original text/speech, untranslated.',
      autoReplaceToggle: 'Replace existing captions',
      autoReplaceHint: 'Clears the current lines before adding new ones. Turn off to keep and add to them.',
      autoDone: 'Made {{count}} caption(s)',
      autoNone: 'No text or speech was found on this video',
      autoStage: {
        read: 'Reading the video…',
        translate: 'Translating…',
        insert: 'Putting it in the video…',
        done: 'Done',
      },
      advancedTitle: 'Advanced options',
      otherSourcesTitle: 'Other ways to add captions',
      sourceTitle: 'Get captions from',
      ocr: 'On-screen text (OCR)',
      ocrHint: 'Read text that is burned into the frame',
      asr: 'Speech in the video',
      asrHint: 'Listen and transcribe the dialogue',
      manual: 'Add manually',
      importSrt: 'Import an .srt file',
      roiTitle: 'Scan area',
      roiFull: 'Whole frame',
      roiCustom: 'Draw an area on the preview',
      roiReset: 'Reset to whole frame',
      interval: 'Scan every',
      intervalHint: 'How far apart the reads are — smaller is more accurate, but slower.',
      language: 'Language',
      languageAuto: 'Detect',
      candidatesTitle: 'What we found',
      candidatesHint:
        'Pick the lines to keep. They are **added to** your existing captions, not a replacement.',
      apply: 'Add {{count}} lines',
      manualHint: 'Use the add button (+) on the Captions track toolbar, or double-click an empty spot on that track.',
      addAtPlayhead: 'Add a line at the playhead',
      textPlaceholder: 'Caption text…',
      editTitle: 'Edit caption',
      visibilityTitle: 'Visibility',
      visibilityToggle: 'Show captions on the preview',
      visibilityHint: 'Hide them to see the video without the overlay; export always burns every caption in regardless.',
      translate: 'Translate',
      translateTitle: 'Re-translate existing captions',
      retranslateHint: 'For changing the language or tone, or translating captions added another way (manual, .srt import). Auto-caption above already translates — no need to run this after it.',
      translateWarning:
        'Translation **overwrites** the current text and cannot be undone. The original is kept for the .srt download.',
      targetLang: 'Translate to',
      tone: 'Tone',
      toneNatural: 'Natural',
      toneFormal: 'Formal',
      toneCasual: 'Casual',
      toneLiteral: 'Literal',
      contextNote: 'Context note',
      contextHint: 'e.g. cooking video, informal address',
      styleTitle: 'Text style',
      styleSelectedTarget: 'Editing line: “{{text}}”. Changes apply straight away.',
      styleDefaultTarget:
        'No line is selected, so this is the default for new lines. Click a line on the timeline to style just that one.',
      styleBody: 'Normal text',
      styleHeadline: 'Headline',
      styleHeadlineHint: 'A headline is big, bold, red text — for the largest line on the video.',
      font: 'Font',
      textColor: 'Text colour',
      bgColor: 'Background',
      autoFitNote: 'Size and position are computed to cover the original text exactly.',
      fontSize: 'Size',
      bold: 'Bold',
      align: 'Align',
      padding: 'Padding',
      applyToAll: 'Apply to all',
      original: 'Original',
      translated: 'Translated',
    },
    voice: {
      title: 'Voice-over',
      speaker: 'Voice',
      preview: 'Preview',
      speed: 'Speed',
      generate: 'Generate voice-over',
      pick: 'Choose a voice',
      groupVieneu: 'VieNeu voices',
      groupCapcut: 'CapCut voices (sometimes paused)',
      done_one: 'Voice-over made for {{count}} line.',
      done_other: 'Voice-over made for {{count}} lines.',
      spedUp_one: '{{count}} line is read faster to fit its caption.',
      spedUp_other: '{{count}} lines are read faster to fit their captions.',
      overflow_one: '{{count}} line is still longer than its caption, so the next one starts a little later.',
      overflow_other: '{{count}} lines are still longer than their captions, so the next ones start a little later.',
      cut_one: 'The end of {{count}} line is cut off where the video ends.',
      cut_other: 'The end of {{count}} lines is cut off where the video ends.',
      skipped_one: 'Skipped {{count}} caption with nothing to read.',
      skipped_other: 'Skipped {{count}} captions with nothing to read.',
      generateHint:
        'Each line starts when its caption appears. A line longer than its caption is read faster to fit, and never runs over the next one.',
      notConfigured: 'Voice-over is not configured on this server.',
      notConfiguredHint: 'Ask an administrator to enable the text-to-speech service.',
      needCaptions: 'Add captions before generating a voice-over.',
      untranslatedWarning:
        'Some lines are not translated. The voice-over reads exactly what is shown.',
      current: 'Current track',
      volume: 'Voice-over volume',
      none: 'No voice-over yet',
    },
    music: {
      title: 'Background music',
      chooseHint: 'Choose a track from your music library.',
      manage: 'Manage your music library',
      unuse: 'Use no music in this project',
      none: 'No music in your library yet',
      emptyHint: 'Upload music once under Account, Music library, then come back here to choose it.',
      openLibrary: 'Open the music library',
      selected: 'In use',
      trim: 'Trim music',
      trimStart: 'Start at',
      trimEnd: 'End at',
      offset: 'Start playing at',
      musicVolume: 'Music volume',
      videoVolume: 'Video volume',
      loop: 'Loop if the music is shorter than the video',
      muteOriginal: 'Mute the video’s own audio',
      startPointTitle: 'Choose where the music starts',
      startPointHint: 'The video is {{videoDuration}} long — the music will be trimmed to exactly {{videoDuration}}, starting from the point you choose below.',
      startPointLabel: 'Start at',
      startPointPreview: 'Preview',
    },
    cut: {
      title: 'Cuts',
      add: 'Add a cut at the playhead',
      addHint: 'Or use I and O to mark the start and end.',
      none: 'No cuts — the export will use the whole video.',
      aiTitle: 'AI suggestions',
      aiHint: 'The AI reads your captions and proposes clips worth keeping.',
      aiTarget: 'Target length',
      aiRun: 'Analyze',
      aiNeedCaptions: 'Add captions before analyzing.',
      aiResults: 'Suggested clips',
      aiScore: 'Score {{score}}',
      useCandidate: 'Use this clip',
      transition: 'Transition',
      markInAt: 'Start point marked: {{time}}',
      transitionNone: 'Hard cut — no effect',
      transitionFast: 'Fast (0.2s)',
      transitionMedium: 'Medium (0.35s)',
      transitionSlow: 'Slow (0.6s)',
      overlapError: 'Cuts must not overlap.',
      tooShort: 'A cut must be at least 1 second long.',
    },
    exportPanel: {
      title: 'Export',
      summary: 'This export will include',
      withCaptions_one: '{{count}} caption',
      withCaptions_other: '{{count}} captions',
      withVoice: 'voice-over',
      withMusic: 'background music',
      withClips_one: '{{count}} cut',
      withClips_other: '{{count}} cuts',
      wholeVideo: 'the whole video',
      nothingToExport: 'Nothing to export — add captions, audio, or a cut.',
      mustSaveFirst: 'Saving your changes before exporting…',
      start: 'Export video',
      started: 'Export started. You can leave this page.',
      history: 'Previous exports',
    },
    shortcutsDialog: {
      title: 'Keyboard shortcuts',
      playPause: 'Play / pause',
      frameStep: 'Step back / forward one frame',
      secondStep: 'Step back / forward one second',
      markIn: 'Mark cut start',
      markOut: 'Mark cut end',
      undo: 'Undo',
      redo: 'Redo',
      save: 'Save now',
      fullscreen: 'Fullscreen',
      help: 'Open this panel',
    },
    mobile: {
      title: 'Compact mode',
      notice:
        'Small screens use compact mode: edit text, run jobs and export. Dragging caption boxes and cut edges needs a larger screen.',
      needsDesktop: 'This needs a larger screen.',
    },
  },

  library: {
    searchPlaceholder: 'Search caption or author',
    downloadFailed: 'Download failed',
    title: 'Library',
    downloads: 'Downloads',
    exports: 'Exports',
    downloadsEmpty: 'No downloads yet',
    downloadsEmptyBody: 'Paste a link on the Download page to get started.',
    exportsEmpty: 'No exports yet',
    exportsEmptyBody: 'Open a project and press Export to render a finished video.',
    saveToComputer: 'Save to this device',
    openOriginal: 'Open original link',
    redownload: 'Download again',
    expired: 'Expired',
    expiresIn: '{{time}} left',
    reopenProject: 'Open project',
    deleteExportTitle: 'Delete this export?',
    deleteExportBody: 'The video file is deleted. The credit record is kept.',
    fromProject: 'From project',
    projectDeleted: 'Project deleted',
  },

  activity: {
    title: 'Activity',
    subtitle: 'Everything you have run, even after a project is deleted.',
    empty: 'No activity yet',
    emptyBody: 'Download a video, or run something in the editor.',
    filterStatus: 'Status',
    filterType: 'Job type',
    filterSource: 'Source',
    sourceEdit: 'Editing',
    sourceDownload: 'Downloads',
    creditsSpent: '{{count}} credits',
    noCredits: 'Free',
    updatedAt: 'Updated',
  },

  explorer: {
    title: 'Explorer',
    subtitle: 'Find profiles on Douyin, TikTok and Bilibili.',
    inputLabel: 'Profile link or UID',
    inputPlaceholder: 'Paste a profile link, an @handle, or a UID',
    find: 'Find',
    detected: 'Detected: {{platform}}',
    notDetected: 'Could not detect a platform from that.',
    tabPosts: 'Posts',
    tabLiked: 'Liked (yours)',
    likedHint:
      'These are videos you liked inside this app, filtered to this author — not their own likes.',
    loadMoreCost: 'Load more (− {{cost}} credits)',
    truncatedWarning: 'Only {{got}} of {{asked}} items loaded — not enough credits for a full page.',
    postsEmpty: 'No posts could be loaded for this profile.',
    saveProfile: 'Save profile',
    saved: 'Saved',
    savedProfiles: 'Saved profiles',
    likedVideos: 'Liked videos',
    likedSubtitle: 'Videos you liked, gathered from every profile.',
    newFolder: 'New folder',
    folderName: 'Folder name',
    rootFolder: 'All',
    maxDepth: 'Folders can only be nested three levels deep.',
    unsaveTitle: 'Remove this profile?',
    unsaveBody: '"{{name}}" will be removed from your saved profiles.',
    savedEmpty: 'No saved profiles',
    savedEmptyBody: 'Open a profile and press Save to add it here.',
    likedEmpty: 'No liked videos yet',
    move: 'Move',
    copy: 'Copy',
    followers: 'Followers',
    following: 'Following',
    videos: 'Videos',
    likes: 'Likes',
    navSearch: 'Find a profile',
    platform: 'Platform',
    library: 'Profile library',
    folderSummary: '{{folders}} folders, {{profiles}} profiles',
    folderMeta: '{{profiles}} profiles, {{folders}} folders',
    folderKind: 'Folder',
    dragHint: 'Drag a profile or a folder onto a folder to move it. Hold Ctrl while dropping a profile to copy it.',
    sectionFolders: 'Folders',
    columnKind: 'Kind',
    columnUpdated: 'Updated',
    sectionProfiles: 'Profiles',
    followersShort: '{{value}} followers',
    videosShort: '{{value}} videos',
    pathMore: 'The folders in between',
    moveFolderTitle: 'Move the folder "{{name}}" to',
    folderMoved: 'Moved the folder "{{name}}" to {{folder}}',
    dropHere: 'Drop here',
    moveTo: 'Move to…',
    copyTo: 'Copy to…',
    removeHere: 'Remove from this folder',
    unsave: 'Unsave',
    renameFolder: 'Rename folder',
    deleteFolder: 'Delete folder',
    deleteFolderTitle: 'Delete the folder "{{name}}"?',
    deleteFolderBody:
      'The profiles and folders inside move out to {{parent}}. No profile is unsaved.',
    folderDeleted: 'Folder deleted. {{profiles}} profiles and {{folders}} folders moved out.',
    folderRenamed: 'Folder renamed',
    folderCreated: 'Created the folder "{{name}}"',
    moved: 'Moved "{{name}}" to {{folder}}',
    copied: 'Copied "{{name}}" to {{folder}}',
    alreadyThere: '"{{name}}" is already in {{folder}}',
    removedHere: 'Removed "{{name}}" from {{folder}}',
    removeLastTitle: 'Unsave "{{name}}"?',
    removeLastBody: 'This is the only place this profile is kept. Removing it unsaves it.',
    folderEmpty: 'This folder is empty',
    folderEmptyBody: 'Drag profiles here, or open a profile and save it to this folder.',
    folderNotFound: 'This folder was not found',
    folderNotFoundBody: 'It may have been deleted.',
    searchSaved: 'Search profiles or folders',
    searchEverywhere: 'Results from the whole library',
    searchNothing: 'No profile or folder matches "{{term}}".',
    inFolder: 'In {{path}}',
    pickMoveTitle: 'Move "{{name}}" to',
    pickCopyTitle: 'Copy "{{name}}" to',
    pickSaveTitle: 'Save "{{name}}" to',
    pickHere: 'Here now',
    pickNewFolder: 'New folder in the selected one',
    pickCreate: 'Create',
    pickTooDeep: 'The selected folder is already on the third level and cannot hold folders.',
    saveTo: 'Save to a folder',
    savedInLabel: 'Saved in',
    saveElsewhere: 'Also save to another folder…',
    savedTo: 'Saved to {{path}}',
    backTo: 'Back to {{name}}',
    openProfile: 'Open profile',
    videosSummary: '{{total}} videos, {{detected}} detected',
    selectUndetected: 'Select videos not detected yet',
    nothingToSelect: 'Every video on the page is detected or already selected.',
  },

  batch: {
    title: 'Batch processing',
    subtitle:
      'Paste many links at once. Each video is downloaded in turn, its captions read, translated and placed in a project. It keeps going when this page or the app is closed.',
    linksLabel: 'Video links',
    linksPlaceholder: 'One Douyin, TikTok or Bilibili link per line. Whole share messages work too.',
    linksHint: 'Up to 500 videos per batch.',
    validLinks_one: '{{count}} usable link',
    validLinks_other: '{{count}} usable links',
    duplicates_one: '{{count}} duplicate',
    duplicates_other: '{{count}} duplicates',
    unsupported_one: '{{count}} unsupported link',
    unsupported_other: '{{count}} unsupported links',
    tooMany: 'A batch takes at most {{max}} videos. Split them into several batches.',
    extract_one: 'Extract captions ({{count}})',
    extract_other: 'Extract captions ({{count}})',
    recent: 'Recent batches',
    noRuns: 'No batches yet',
    noRunsBody: 'Paste links above and press Extract captions to start.',
    status: 'Status',
    progress: 'Progress',
    failedShort: 'Failed',
    createdAt: 'Created',
    progressText: '{{done}} of {{total}} videos processed',
    remaining: 'About {{time}} left',
    setupTitle_one: 'Extract captions for {{count}} video',
    setupTitle_other: 'Extract captions for {{count}} videos',
    setupBody: 'Choose how each video\'s captions are read. Videos are processed one after another and the captions go straight into their projects.',
    name: 'Batch name',
    namePlaceholder: 'For example: Running channel, October',
    translate: 'Translate into Vietnamese',
    readAll: 'Read captions from',
    readOne: 'How the captions of video {{index}} are read',
    atMost: 'At most',
    start_one: 'Start ({{count}} video)',
    start_other: 'Start ({{count}} videos)',
    mode: {
      auto: 'Automatic',
      ocr: 'On-screen text',
      asr: 'Speech',
    },
    modeShort: {
      auto: 'Automatic',
      ocr: 'Text',
      asr: 'Speech',
    },
    modeHint: {
      auto: 'Reads the text on the video. A video with no text is listened to instead.',
      ocr: 'Only the text shown on the video (OCR).',
      asr: 'Only the speech in the video.',
      mixed: 'Each video its own way, chosen in the list below.',
    },
    runStatus: {
      running: 'Running',
      paused: 'Paused',
      pausedCredits: 'Out of credits',
      done: 'Finished',
      cancelled: 'Cancelled',
    },
    itemStatus: {
      queued: 'Waiting',
      fetching: 'Downloading',
      reading: 'Reading the text',
      listening: 'Listening',
      done: 'Done',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    translated: 'Captions are translated into Vietnamese.',
    notTranslated: 'Captions keep their original language.',
    pause: 'Pause',
    resume: 'Resume',
    retryFailed_one: 'Retry {{count}} failed video',
    retryFailed_other: 'Retry {{count}} failed videos',
    cancel: 'Cancel the rest',
    cancelTitle: 'Cancel the videos not started yet?',
    cancelBody_one: '{{count}} waiting video will not be processed. Finished and running videos keep their results.',
    cancelBody_other: '{{count}} waiting videos will not be processed. Finished and running videos keep their results.',
    creditsPaused:
      'Today\'s credits ran out, so the batch is paused. Waiting videos carry on from where they stopped (no second download charge) when you press Resume once there are credits.',
    stat: {
      done: 'Done',
      working: 'Working',
      waiting: 'Waiting',
      failed: 'Failed',
    },
    readerPc: 'Captions are being read on your computer (the VideoGrab app), using all of it.',
    readerServer:
      'Captions are being read on the server. You can close this page; the queue keeps running.',
    filter: 'Filter videos',
    filters: {
      all: 'All',
      active: 'Working',
      done: 'Done',
      failed: 'Failed',
    },
    nothingHere: 'No videos here',
    video: 'Video',
    readFrom: 'Read from',
    result: 'Result',
    lines_one: '{{count}} caption line',
    lines_other: '{{count}} caption lines',
    kept_one: 'Kept existing captions ({{count}} line)',
    kept_other: 'Kept existing captions ({{count}} lines)',
    noCaptions: 'No captions found',
    addLinks: 'Add links to this batch',
    addHint: 'New links join the end of the queue and are processed right after the videos waiting.',
    addButton_one: 'Add to batch ({{count}})',
    addButton_other: 'Add to batch ({{count}})',
    addTitle_one: 'Add {{count}} video to the batch',
    addTitle_other: 'Add {{count}} videos to the batch',
    addBody: 'The videos join the end of the batch\'s queue and their captions are read the way you choose.',
    addSubmit_one: 'Add to the queue ({{count}})',
    addSubmit_other: 'Add to the queue ({{count}})',
    added_one: 'Added {{count}} video to the queue',
    added_other: 'Added {{count}} videos to the queue',
    addedNone: 'These links are already in the batch',
    target: 'Put the links in',
    targetAppend: 'The batch that is running',
    targetNew: 'A new batch',
    targetNewHint: 'Runs after the batches already going',
    manyLinks: 'More than 30 links? Use batch processing',
  },

  detect: {
    action_one: 'Detect {{count}} video',
    action_other: 'Detect {{count}} videos',
    button: 'Detect',
    detected: 'Detected',
    detecting: 'Detecting',
    hasProject: 'In a project',
    lines_one: '{{count}} line',
    lines_other: '{{count}} lines',
    openProject: 'Open the project',
    started_one: 'Detecting {{count}} video. Follow it here or in Projects.',
    started_other: 'Detecting {{count}} videos. Follow them here or in Projects.',
    kept_one: '{{count}} video already detected was left as it is (its captions are kept).',
    kept_other: '{{count}} videos already detected were left as they are (their captions are kept).',
    failed_one: '{{count}} video could not be detected: {{reason}}',
    failed_other: '{{count}} videos could not be detected: {{reason}}',
    viewProjects: 'View in Projects',
    allKept: 'These videos are already detected. Open them from their cards.',
  },

  account: {
    title: 'Account',
    subtitle: 'Your profile, security, devices and credits.',
    profile: 'Profile & appearance',
    security: 'Security',
    api: 'API',
    credits: 'Credits',
    displayName: 'Display name',
    displayNameReadonly: 'Your display name is set by an administrator.',
    language: 'Language',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'Match system',
    apiKeys: 'API keys',
    apiKeysEmpty: 'No keys yet',
    createKey: 'Create a key',
    keyName: 'Label',
    keyCreatedTitle: 'Your new key',
    keyCreatedBody: 'This is the only time the full key is shown. Copy it now.',
    revokeTitle: 'Revoke this key?',
    revokeBody: 'Anything using "{{name}}" stops working immediately.',
    keyLimit: 'Up to 10 active keys.',
    apiDocs: 'API documentation',
    apiDocsIntro: 'Authenticate with the header Authorization: Bearer <key>.',
    apiScopeNote: 'API keys cannot call admin, account or key-management endpoints.',
    lastUsed: 'Last used',
    neverUsed: 'Never used',
    musicTab: 'Music library',
    securityPage: {
      intro: 'Changing your password signs out every other session of yours.',
      afterTitle: 'After you change it',
      afterSessions: 'Other browsers and devices have to sign in again with the new password.',
      afterDevices: 'Computers with VideoGrab installed stop taking work, and reconnect when you sign in on them.',
    },
    music: {
      title: 'Music library',
      intro: 'Music uploaded here can be used in every project. In the editor you only choose a track.',
      upload: 'Upload music',
      uploading: 'Uploading {{percent}}%',
      formats: 'MP3, WAV, M4A, AAC or OGG, up to {{max}} MB per track.',
      used: '{{used}} of {{limit}} tracks.',
      full: 'Your library holds {{limit}} tracks, which is the limit. Remove one you do not use to upload more.',
      empty: 'No music in your library yet',
      emptyHint: 'Upload your first track. Each track is uploaded once, up to {{max}} MB.',
      play: 'Listen',
      stop: 'Stop',
      remove: 'Remove this track',
      removeTitle: 'Remove from your music library?',
      removeBody: '“{{name}}” will disappear from every project that uses it.',
      removeConfirm: 'Remove from library',
      removed: 'Removed from your library',
      added: 'Added to your library',
      alreadyInLibrary: 'This track is already in your library',
      playFailed: 'This track could not be played. Try again.',
      backedUp: 'Every track is backed up.',
      backupPending: '{{pending}} waiting to be backed up. This runs by itself; there is nothing you need to do.',
      backupWaiting: 'Backup pending',
    },
    devicesTab: 'My devices',
    devices: {
      title: 'My devices',
      intro: 'A computer with VideoGrab installed may process jobs started inside the desktop app. Browser jobs always run on the server.',
      empty: 'No devices yet',
      emptyHint: 'Open the VideoGrab app on your computer and sign in. It will show up here.',
      online: 'Active now',
      lastSeen: 'Last active {{when}}',
      neverConnected: 'Never connected',
      backendGpu: 'Reads with the graphics card',
      backendCpu: 'Reads with the CPU',
      readers_one: '{{count}} reader',
      readers_other: '{{count}} readers side by side',
      speed: '{{fps}} frames a second',
      taskOcr: 'Reads text on video',
      taskAsr: 'Transcribes speech',
      tooSlow: 'This computer reads slower than the server, so text is still read on the server.',
      routingOff: 'An administrator has switched off processing on users\' computers. Everything runs on the server.',
      revoke: 'Remove this device',
      revokeTitle: 'Remove this device?',
      revokeBody: '"{{name}}" stops taking work at once. Anything it was doing is finished by the server.',
      revoked: 'Device removed',
    },
  },

  support: {
    title: 'Support',
    subtitle: 'Report a problem or suggest something — we read all of it.',
    type: 'Request type',
    typeBug: 'Report a bug',
    typeUi: 'Interface feedback',
    typeFeature: 'Feature request',
    typeOther: 'Something else',
    content: 'Details',
    contentPlaceholder: 'Be as specific as you can: what you were doing, and what happened.',
    submit: 'Send request',
    submitted: 'Sent. Thank you!',
    myRequests: 'Your requests',
    empty: 'You have not sent any requests',
    statusOpen: 'Open',
    statusResolved: 'Resolved',
  },

  admin: {
    users: 'Users',
    invite: 'Invite a user',
    inviteEmail: 'Email',
    inviteName: 'Display name',
    inviteLimit: 'Daily credit limit',
    inviteLimitHint: 'Leave blank to use the system default.',
    inviteSent: 'Account created. Default password: {{password}}',
    role: 'Role',
    lastLogin: 'Last sign-in',
    features: 'Features',
    lock: 'Lock',
    unlock: 'Unlock',
    lockTitle: 'Lock this account?',
    lockBody: '{{email}} will be signed out everywhere and cannot sign in again.',
    unlockTitle: 'Unlock this account?',
    unlockBody: '{{email}} will be able to sign in again.',
    resetPassword: 'Reset password',
    resetTitle: 'Reset this password?',
    resetBody:
      '{{email}} gets the default password and must change it on next sign-in. All current sessions end.',
    setLimit: 'Change limit',
    downloadHistory: 'Download history',
    integrations: 'Platform integrations',
    integrationsHint:
      'Cookies used to call the platforms’ APIs. Read-only — they are configured in .env.',
    cookieStatus: 'Status',
    cookieConfigured: 'Configured',
    cookieMissing: 'Not set',
    cookieReadOnlyTitle: 'Cookies are configured in .env only',
    cookieReadOnlyBody:
      'Edit the matching variable in {{path}}, then restart the server to apply it. This page reports status only; a cookie value never leaves the server.',
    cookieEnvVar: 'Variable in use',
    cookieMissingHint: '{{vars}} is not set in .env — this platform’s features will fail.',
    cookieNames: 'Keys in the cookie',
    cookieNamesMore_one: '+{{count}} more key',
    cookieNamesMore_other: '+{{count}} more keys',
    cookieMissingCount_one: '{{count}} platform has no cookie',
    cookieMissingCount_other: '{{count}} platforms have no cookie',
    feedbackInbox: 'Feedback inbox',
    markResolved: 'Mark resolved',
    reopen: 'Reopen',
    noFeedback: 'No feedback',
    feedbackEmailPlaceholder: 'Filter by email',
    roles: {
      admin: 'Admin',
      user: 'User',
    },
    userStatus: {
      active: 'Active',
      locked: 'Locked',
      pending: 'Pending',
      deactivated: 'Deactivated',
    },
  },

  a11y: {
    mainNavigation: 'Main navigation',
    userMenu: 'Account menu',
    jobTray: 'Job tray',
    creditBalance: 'Credit balance',
    closeDialog: 'Close dialog',
    loading: 'Loading content',
    progress: '{{percent}} percent complete',
    progressIndeterminate: 'Working, progress unknown',
  },
};
